import { useRef, useState } from 'react'
import { RotateCcw, CopyCheck, Link2, Unlink } from 'lucide-react'
import {
  CROP_OPS,
  DEFAULT_OP,
  FOCUS_STEP,
  MAX_BOX,
  MIN_BOX,
  OUTPUT_FORMATS,
  SIZING_STEP,
  applyBoxToAll,
  clearBox,
  conformToRatio,
  FOCUS_ANCHORS,
  focusPoint,
  isRatioLocked,
  normalizeBox,
  normalizeSizing,
  ratioForcedByOp,
  resolveBox,
  setBox,
  setFocus,
  setSizingOption,
  sizingOf,
  snap,
  supportsFocus,
  withSizing,
} from '../editable/imageSizing'
import { RESPONSIVE_DEVICES } from '../editable/responsiveVisibility'

/**
 * `image-sizing` — l'éditeur ouvert par l'engrenage de la poignée d'image.
 *
 * Il règle ce que le glissé ne peut pas dire : le STYLE DE CADRAGE (comment
 * l'image remplit sa boîte), le FORMAT de la dérivée, et les trois boîtes au
 * chiffre près — parce qu'une valeur exacte se tape, elle ne se vise pas à la
 * souris. Le POINT D'INTÉRÊT, lui, se vise très bien à la souris (on déplace
 * l'image dans son cadre, sur le canevas) : il n'est ici que pour être réglé
 * au chiffre, recentré, et pour exister quand on n'a pas de pointeur.
 *
 * Il reçoit et renvoie LA VALEUR D'IMAGE ENTIÈRE : le `sizing` vit dedans, à
 * côté du `mid`, et `mid`/`alt` doivent traverser l'éditeur intacts — c'est la
 * médiathèque qui choisit le média, pas lui.
 *
 * Une bande laissée vide n'est pas une bande à zéro : elle HÉRITE de la plus
 * large réglée (`resolveBox`), et le champ affiche cette valeur en filigrane.
 */

const DEVICE_LABELS = { mobile: 'Mobile', tablet: 'Tablette', desktop: 'Ordinateur' }

export const ImageSizingEditor = ({
  value,
  device = 'desktop',
  fallbackBox = null,
  sourceRatio = 0,
  renderedOp = null,
  onSave,
  onClose,
}) => {
  // Une image sans réglage n'est pas une image sans cadrage : elle rend celui
  // que le YAML du bloc déclare, et c'est LUI que le canevas transmet
  // (`renderedOp`). L'amorcer ici, c'est montrer le style réellement à l'œuvre
  // au lieu du défaut de la poignée — et surtout ne pas le changer dans le dos
  // de l'éditeur quand il ne fait que taper une taille.
  const [sizing, setSizing] = useState(() => sizingOf(value) ?? (renderedOp ? { op: renderedOp } : null))
  // La frappe en cours, telle quelle. Normaliser à CHAQUE touche rendait les
  // champs inutilisables : taper « 6 » donnait aussitôt 20 (le minimum), puis
  // « 40 » donnait « 2040 », et vider un champ ne faisait rien. La valeur n'est
  // bornée qu'à la sortie du champ — un champ vidé remet la bande en héritage.
  const [typing, setTyping] = useState({})

  const typeKey = (target, key) => `${target}.${key}`

  /**
   * La boîte de départ d'une bande : la sienne, l'héritée, sinon LA TAILLE
   * RENDUE que l'appelant a mesurée. Sans ce dernier recours, taper une seule
   * dimension sur une image jamais dimensionnée n'écrivait rien — une boîte a
   * besoin de ses deux côtés — et le champ se vidait tout seul au blur.
   */
  const seedBox = (target) => sizing?.[target] ?? resolveBox(sizing, target) ?? fallbackBox ?? { w: 0, h: 0 }

  const onType = (target, key, raw) => {
    setTyping(t => ({ ...t, [typeKey(target, key)]: raw }))
    const n = Number(raw)
    // On n'écrit en cours de frappe qu'une valeur DÉJÀ dans les bornes : le
    // reste attend le blur, sinon on réécrirait sous les doigts.
    if (!raw || !Number.isFinite(n) || n < MIN_BOX || n > MAX_BOX) return
    const current = seedBox(target)
    const next = keepRatio(normalizeBox({ ...current, [key]: n }), key, typingRatio.current)
    if (next) setSizing(setBox(sizing, target, next))
  }

  const onCommitField = (target, key) => {
    const raw = typing[typeKey(target, key)]
    setTyping(t => { const { [typeKey(target, key)]: _drop, ...rest } = t; return rest })
    if (raw === undefined) return
    // Champ vidé = la bande repart en héritage, elle ne vaut pas zéro.
    if (raw === '') { setSizing(clearBox(sizing, target)); return }
    const current = seedBox(target)
    const next = keepRatio(normalizeBox({ ...current, [key]: raw }), key, typingRatio.current)
    if (next) setSizing(setBox(sizing, target, next))
  }

  const currentBox = resolveBox(sizing, device)
  const op = sizing?.op ?? renderedOp ?? DEFAULT_OP
  // Le point d'intérêt : toujours une valeur à afficher — le centre quand rien
  // n'est réglé, ce qui est exactement ce que rend un recadrage sans point.
  const focus = focusPoint(sizing)
  const ext = sizing?.ext ?? 'webp'
  const locked = isRatioLocked(sizing)
  // « Contenir » impose les proportions de la source : le serveur met l'image à
  // l'échelle sans jamais la déformer, donc une autre boîte n'ajouterait que des
  // bandes vides. Le bouton est alors désactivé, et il le dit.
  const ratioForced = ratioForcedByOp(op)
  // Le rapport tenu pendant qu'on TAPE : figé à l'entrée dans le champ, sinon il
  // dériverait à chaque chiffre (640 → 64 → 6 en donnerait trois différents).
  const typingRatio = useRef(0)

  const ratioFor = (target) => {
    if (sourceRatio && ratioForced) return sourceRatio
    const box = sizing?.[target] ?? resolveBox(sizing, target) ?? fallbackBox
    return box ? box.w / box.h : 0
  }

  /** L'autre côté suit, quand les proportions sont liées. */
  const keepRatio = (box, key, ratio) => {
    if (!locked || !ratio || !box) return box
    // Le côté DÉDUIT est calé sur le pas du quantizer : c'est lui qui partira
    // dans l'URL, autant l'afficher tel quel. Le côté tapé n'est jamais
    // corrigé sous les doigts.
    return key === 'w'
      ? normalizeBox({ w: box.w, h: snap(box.w / ratio) })
      : normalizeBox({ w: snap(box.h * ratio), h: box.h })
  }

  /**
   * Changer de style de cadrage peut changer ce qu'une boîte SIGNIFIE : en
   * passant à « Contenir », on ramène les boîtes au rapport de l'original,
   * sinon l'image s'afficherait entourée de vide.
   */
  /**
   * Ce qui part dans la valeur. Un `sizing` qui ne porte QUE le style déjà
   * rendu n'est pas un réglage : c'est l'état d'ouverture de la pop-in (amorcé
   * sur `renderedOp`), et l'écrire graverait dans le contenu une op que le YAML
   * du bloc déclare déjà — qui ne bougerait plus le jour où le bloc change.
   */
  const toSave = () => {
    const next = normalizeSizing(sizing)
    const bare = next && next.op === renderedOp && !next.focus && !next.ext
      && !RESPONSIVE_DEVICES.some(d => next[d])
    return bare ? null : next
  }

  const chooseOp = (nextOp) => {
    const withOp = setSizingOption(sizing, 'op', nextOp)
    setSizing(ratioForcedByOp(nextOp) && sourceRatio ? conformToRatio(withOp, sourceRatio) : withOp)
  }

  /**
   * La pop-in tient dans 26 rem de large et doit tenir SANS DÉFILEMENT : cinq
   * réglages, dont trois listes de choix. D'où la densité de ce rendu —
   * segments plutôt que lignes empilées, l'explication du SEUL choix actif, une
   * phrase d'aide par section — et la barre d'actions collée en bas, pour que
   * « Appliquer » reste sous la main quand le contenu, lui, défile.
   */
  const legendClass = 'text-xs font-medium text-gray-600 mb-1.5'
  const choiceClass = (active) => `rounded-lg border px-2 py-1.5 text-sm transition-all ${
    active
      ? 'border-teal-600 bg-teal-50 text-teal-800 font-medium'
      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
  }`

  return (
    <div className="flex flex-col gap-3.5" onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}>

      {/* Style de cadrage — trois segments, et l'explication du seul choix actif :
          empilées, les trois descriptions prenaient un tiers de la pop-in. */}
      <fieldset>
        <legend className={legendClass}>Style de cadrage</legend>
        <div className="grid grid-cols-3 gap-1">
          {CROP_OPS.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => chooseOp(opt.id)}
              aria-pressed={op === opt.id}
              className={choiceClass(op === opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {CROP_OPS.find(o => o.id === op)?.hint}
        </p>
      </fieldset>

      {/* Point d'intérêt — quelle part de l'image reste dans le cadre.
          Absent sous « Contenir » : rien n'y est jeté, il n'y a rien à viser. */}
      {supportsFocus(op) && (
        <fieldset>
          <legend className={legendClass}>Position dans le cadre</legend>
          <div className="flex items-center gap-3">
            {/* Les neuf ancrages : « garde le haut », « garde la gauche »… Un
                clic sûr là où le glissé demande d'aller buter en bout de
                course. La cellule du centre est le « recentrer ». */}
            <div
              role="group"
              aria-label="Aligner l’image dans son cadre"
              className="grid shrink-0 grid-cols-3 gap-1"
            >
              {FOCUS_ANCHORS.map(anchor => {
                const active = focus.x === anchor.x && focus.y === anchor.y
                return (
                  <button
                    key={anchor.id}
                    type="button"
                    onClick={() => setSizing(setFocus(sizing, { x: anchor.x, y: anchor.y }, op))}
                    aria-pressed={active}
                    title={anchor.label}
                    aria-label={anchor.label}
                    className={`flex h-7 w-7 items-center justify-center rounded border transition-all ${
                      active
                        ? 'border-teal-600 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-teal-600' : 'bg-gray-300'}`}
                    />
                  </button>
                )
              })}
            </div>
            <div className="flex flex-1 flex-col gap-2">
              {[
                { key: 'x', label: 'Horizontale', from: 'gauche', to: 'droite' },
                { key: 'y', label: 'Verticale', from: 'haut', to: 'bas' },
              ].map(axis => (
                <div key={axis.key} className="flex items-center gap-2">
                  <span className="w-16 shrink-0 text-xs text-gray-600">{axis.label}</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={FOCUS_STEP}
                    value={focus[axis.key]}
                    onChange={(e) => setSizing(setFocus(sizing, { ...focus, [axis.key]: Number(e.target.value) }, op))}
                    aria-label={`Position ${axis.label.toLowerCase()} du cadre — 0 % : ${axis.from}, 100 % : ${axis.to}`}
                    className="min-w-0 flex-1 accent-teal-600"
                  />
                  <span className="w-10 shrink-0 text-right text-xs text-gray-600 tabular-nums">
                    {focus[axis.key]} %
                  </span>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Sur le canevas, il se règle aussi en faisant glisser l’image dans son cadre.
          </p>
        </fieldset>
      )}

      {/* Tailles par point de rupture — la bande visible dans le canevas est mise en avant */}
      <fieldset>
        <legend className={legendClass}>Taille par point de rupture (px)</legend>
        {/* Lier les proportions est le DÉFAUT : redimensionner à la main ne
            déforme alors jamais l'image, et taper une largeur déduit la
            hauteur. On délie pour recadrer volontairement — c'est ce qui donne
            un portrait sur mobile et un panoramique sur ordinateur à partir de
            la même photo. */}
        <div className="mb-1 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSizing(setSizingOption(sizing, 'lockRatio', !locked))}
            disabled={ratioForced}
            aria-pressed={locked}
            className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
              locked
                ? 'border-teal-600 bg-teal-50 text-teal-800 font-medium'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
            }`}
          >
            {locked
              ? <Link2 size={13} aria-hidden="true" />
              : <Unlink size={13} aria-hidden="true" />}
            {locked ? 'Proportions liées' : 'Proportions déliées'}
          </button>
          <span className="text-xs text-gray-500">
            {ratioForced
              ? '« Contenir » les garde toujours.'
              : (locked ? 'L’autre côté suit.' : 'Les deux côtés sont libres.')}
          </span>
        </div>
        <div className="flex flex-col">
          {RESPONSIVE_DEVICES.map(target => {
            const own = sizing?.[target] ?? null
            const shown = own ?? resolveBox(sizing, target)
            return (
              <div
                key={target}
                className={`flex items-center gap-2 rounded-lg px-2 py-1 ${
                  target === device ? 'bg-teal-50/60' : ''
                }`}
              >
                <span className="w-20 shrink-0 text-xs text-gray-600">
                  {DEVICE_LABELS[target]}
                </span>
                <input
                  type="number"
                  step={SIZING_STEP}
                  min={MIN_BOX}
                  max={MAX_BOX}
                  value={typing[`${target}.w`] ?? own?.w ?? ''}
                  placeholder={shown ? String(shown.w) : 'auto'}
                  onFocus={() => { typingRatio.current = ratioFor(target) }}
                  onChange={(e) => onType(target, 'w', e.target.value)}
                  onBlur={() => onCommitField(target, 'w')}
                  aria-label={`Largeur — ${DEVICE_LABELS[target]}`}
                  className="w-full min-w-0 rounded-md border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <span className="text-xs text-gray-500" aria-hidden="true">×</span>
                <input
                  type="number"
                  step={SIZING_STEP}
                  min={MIN_BOX}
                  max={MAX_BOX}
                  value={typing[`${target}.h`] ?? own?.h ?? ''}
                  placeholder={shown ? String(shown.h) : 'auto'}
                  onFocus={() => { typingRatio.current = ratioFor(target) }}
                  onChange={(e) => onType(target, 'h', e.target.value)}
                  onBlur={() => onCommitField(target, 'h')}
                  aria-label={`Hauteur — ${DEVICE_LABELS[target]}`}
                  className="w-full min-w-0 rounded-md border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button
                  type="button"
                  onClick={() => setSizing(clearBox(sizing, target))}
                  disabled={!own}
                  title={`Réinitialiser ${DEVICE_LABELS[target]} (hérite alors du point de rupture le plus large)`}
                  aria-label={`Réinitialiser ${DEVICE_LABELS[target]}`}
                  className="builder-row-action shrink-0 disabled:opacity-30"
                >
                  <RotateCcw size={13} aria-hidden="true" />
                </button>
              </div>
            )
          })}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Une taille vide hérite du point de rupture le plus large.
        </p>
        {currentBox && (
          <button
            type="button"
            onClick={() => setSizing(applyBoxToAll(sizing, currentBox))}
            className="mt-1 flex items-center gap-1.5 text-xs text-teal-700 hover:text-teal-900"
          >
            <CopyCheck size={13} aria-hidden="true" />
            Appliquer {currentBox.w} × {currentBox.h} aux trois points de rupture
          </button>
        )}
      </fieldset>

      {/* Format de sortie — la dérivée est produite dans ce format, quel que soit
          l'original. Deux boutons : ils tiennent sur la ligne de leur intitulé. */}
      <fieldset className="flex items-center gap-2">
        <legend className="sr-only">Format de sortie</legend>
        <span className="w-20 shrink-0 text-xs font-medium text-gray-600" aria-hidden="true">
          Format
        </span>
        {OUTPUT_FORMATS.map(fmt => (
          <button
            key={fmt.id}
            type="button"
            onClick={() => setSizing(setSizingOption(sizing, 'ext', fmt.id))}
            aria-pressed={ext === fmt.id}
            aria-label={`Format de sortie ${fmt.label}`}
            className={`${choiceClass(ext === fmt.id)} px-3 py-1 text-xs`}
          >
            {fmt.label}
          </button>
        ))}
      </fieldset>

      {/* Collée en bas : le contenu défile quand la pop-in touche le bord de
          l'écran, « Appliquer » ne doit pas partir avec lui. Les marges
          négatives reprennent le rembourrage du corps de la pop-in, et le `mt-4`
          compense ce que le collage lui fait remonter — sans lui, la barre mordait
          de deux pixels sur la ligne du dessus. */}
      <div className="sticky bottom-0 -mx-4 -mb-4 mt-4 flex justify-between gap-2 border-t border-gray-100 bg-white px-4 py-2.5">
        <button
          type="button"
          onClick={() => { onSave(withSizing(value, null)); onClose() }}
          disabled={!sizing}
          className="btn btn-ghost"
        >
          Retirer les réglages
        </button>
        <span className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => { onSave(withSizing(value, toSave())); onClose() }}
            className="btn btn-primary"
          >
            Appliquer
          </button>
        </span>
      </div>
    </div>
  )
}
